'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const useBlockNavigation = (shouldBlock: boolean, allowedRoutes: string[] = []) => {
  const router = useRouter();
  const pathname = usePathname();
  const [isAttemptingNavigation, setIsAttemptingNavigation] = useState(false);

  const originalPushRef = useRef(router.push); // Store original router.push
  const lastLocationRef = useRef<string | null>(null); // Track last known location

  const canNavigate = (url: string) => {
    const { pathname: targetPathname } = new URL(url, window.location.origin);
    return allowedRoutes.some(
      (route) => targetPathname === route || targetPathname?.startsWith(`${route}/`),
    );
  };

  useEffect(() => {
    const handleNavigation = (url: string) => {
      // ✅ If navigation is allowed, proceed immediately
      if (!shouldBlock || canNavigate?.(url) || url === pathname) {
        originalPushRef.current(url); // Use original router.push
        return;
      }

      // 🚧 Block navigation & ask for confirmation
      setIsAttemptingNavigation(true);
    };

    // Override router.push **only once**
    router.push = ((url) => {
      handleNavigation(url);
    }) as typeof router.push;

    return () => {
      router.push = originalPushRef.current; // Restore original push
    };
  }, [shouldBlock, pathname, allowedRoutes]);

  // 🔹 Handle Reload Prevention
  // useEffect(() => {
  //   const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //     if (shouldBlock) {
  //       event.preventDefault();
  //       event.returnValue = 'Are you sure you want to leave?';
  //     }
  //   };

  //   window.addEventListener('beforeunload', handleBeforeUnload);

  //   return () => {
  //     window.removeEventListener('beforeunload', handleBeforeUnload);
  //   };
  // }, [shouldBlock]);

  // 🔙 Handle Back Button with Previous Route Tracking
  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      if (shouldBlock) {
        event.preventDefault();

        setIsAttemptingNavigation(true);

        history.pushState(null, '', window.location.href); // Keep user on the same page
      }
    };

    lastLocationRef.current = pathname; // Track last known location
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [shouldBlock, pathname]);

  const proceedNavigation = (route: string) => {
    setIsAttemptingNavigation(false);
    route && originalPushRef.current(route);
  };

  const cancelNavigation = () => {
    setIsAttemptingNavigation(false);
  };

  return { isAttemptingNavigation, proceedNavigation, cancelNavigation };
};

export default useBlockNavigation;
